"use client";

import React, { createContext, useContext, useEffect, useMemo, useState, Suspense } from "react";

import { Loader } from "@/dashboard/components/ui/loader";
import { useMeQuery } from "@/dashboard/hooks/auth/use-me-query";
import { useSignIn } from "@/dashboard/hooks/auth/use-sign-in";
import { useSignOut } from "@/dashboard/hooks/auth/use-sign-out";
import { useSelectTenant } from "@/dashboard/hooks/tenant/use-select-tenant";
import { isHttpError } from "@/dashboard/lib/api/errors";
import { ALL_TENANTS_ID } from "@/dashboard/lib/auth/constants";
import { getRoleForTenant } from "@/dashboard/lib/auth/role-helpers";
import type { Tenant } from "@/dashboard/types/tenant";
import type { User } from "@/dashboard/types/user";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
type LogoutReason = "manual" | "session-expired" | null;

interface AuthContextShape {
  user: User | null;
  tenants: Tenant[];
  /**
   * null → auth still loading
   * "ALL" → multi-tenant mode
   * uuid → single-tenant mode
   */
  activeTenantId: string | null;
  authResolved: boolean;
  isAuthenticated: boolean;
  isSwitchingTenant: boolean;
  logoutReason: LogoutReason;

  /* actions */
  signIn: (
    args: Parameters<ReturnType<typeof useSignIn>["mutateAsync"]>[0]
  ) => ReturnType<ReturnType<typeof useSignIn>["mutateAsync"]>;
  signOut: () => Promise<unknown>;
  switchTenant: (id: string) => Promise<string>;
}

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */
const AuthContext = createContext<AuthContextShape>({} as AuthContextShape);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */
export default function AuthProvider({ children }: { children: React.ReactNode }) {
  /* ---------- local state ---------- */
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [logoutReason, setLogoutReason] = useState<LogoutReason>(null);

  /* ---------- hooks ---------- */
  const meQuery = useMeQuery();

  const signInMut = useSignIn();
  const signOutMut = useSignOut(() => setLogoutReason("manual"));

  const { mutateAsync: switchTenant, isPending: isSwitchingTenant } =
    useSelectTenant(setActiveTenantId);

  /* ------------------------------------------------------------------
   * Clear logout-reason after a successful auth refresh
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (meQuery.data?.user) {
      setLogoutReason(null);
    }
  }, [meQuery.data?.user]);

  /* ------------------------------------------------------------------
   * Mark session-expired on 401 responses
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (meQuery.isError && isHttpError(meQuery.error) && meQuery.error.status === 401) {
      setLogoutReason("session-expired");
    }
  }, [meQuery.isError, meQuery.error]);

  /* ------------------------------------------------------------------
   * Derive the effective tenant ID
   * ------------------------------------------------------------------ */
  const serverActiveId = meQuery.data?.activeTenantId as typeof ALL_TENANTS_ID | string | null;

  const effectiveTenantId: string | null = !meQuery.isFetched
    ? null
    : (activeTenantId ??
      serverActiveId ??
      (meQuery.data?.tenants?.length === 1 ? meQuery.data.tenants[0]!.id : ALL_TENANTS_ID));

  /* ------------------------------------------------------------------
   * Auto-switch to valid tenant if current one is not allowed
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const user = meQuery.data?.user;
    const tenants = meQuery.data?.tenants ?? [];
    
    if (!user || !effectiveTenantId || effectiveTenantId === ALL_TENANTS_ID || !meQuery.isFetched) {
      return;
    }

    // Check if user has permission for the current tenant
    const hasPermission = user.superAdmin || getRoleForTenant(user, effectiveTenantId) !== null;
    
    if (!hasPermission) {
      // Find first tenant the user has permissions for
      const allowedTenant = tenants.find(tenant => 
        user.superAdmin || getRoleForTenant(user, tenant.id) !== null
      );
      
      if (allowedTenant) {
        // Auto-switch to the first allowed tenant
        void switchTenant(allowedTenant.id);
      } else if (tenants.length > 1) {
        // If no specific tenant is allowed but there are multiple, switch to "All Tenants"
        void switchTenant(ALL_TENANTS_ID);
      }
    }
  }, [meQuery.data?.user, meQuery.data?.tenants, effectiveTenantId, meQuery.isFetched, switchTenant]);

  /* ------------------------------------------------------------------
   * Stable function references
   * ------------------------------------------------------------------ */
  const signInFn = signInMut.mutateAsync;
  const signOutFn = signOutMut.mutateAsync;
  const switchTenantFn = switchTenant;

  /* ---------- memoized context ---------- */
  const ctx = useMemo<AuthContextShape>(
    () => ({
      user: meQuery.data?.user ?? null,
      tenants: meQuery.data?.tenants ?? [],
      activeTenantId: effectiveTenantId,
      authResolved: meQuery.isFetched,
      isAuthenticated: !!meQuery.data?.user,
      isSwitchingTenant,
      logoutReason,

      signIn: signInFn,
      signOut: signOutFn,
      switchTenant: switchTenantFn,
    }),
    [
      meQuery.data,
      meQuery.isFetched,
      effectiveTenantId,
      isSwitchingTenant,
      logoutReason,
      signInFn,
      signOutFn,
      switchTenantFn,
    ]
  );

  return (
    <Suspense fallback={<Loader variant="fullscreen" />}>
      <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>
    </Suspense>
  );
}
